import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useParams } from "react-router-dom";
import { message } from "antd";
import {
  Add,
  Remove,
  Description,
  Delete,
  Edit,
  CalendarToday,
} from "@mui/icons-material";

const Salary = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  // const navigate = useNavigate();
  const [studentData, setStudentData] = React.useState({
    name: "",
    base_salary: 0,
    salaries: [],
  });
  const token = localStorage.getItem("token");

  // State for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [salaryToDelete, setSalaryToDelete] = React.useState(null);

  // State for edit mode
  const [editMode, setEditMode] = React.useState(false);
  const [editingSalaryId, setEditingSalaryId] = React.useState(null);

  const [salary, setSalary] = React.useState(0);
  const [lectures_count, setLectures_count] = React.useState(0);
  const [addition, setAddition] = React.useState(0);
  const [deduction, setDeduction] = React.useState(0);
  const [reason, setReason] = React.useState("");
  const [date, setDate] = React.useState(
    new Date().toISOString().split("T")[0]
  );

  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  // Fetch teacher data and salaries
  React.useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://api.clever-school.com/api/teachers/${id}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (isMounted) {
          const userData = response.data;
          setStudentData({
            name: userData.name || "",
            phone_number: userData.phone_number || "",
            base_salary: userData.base_salary || "",
            // base_lectures_count: userData.base_lectures_count || "",
            salaries: userData.salaries || [],
          });

          // Set form fields with base values
          setSalary(parseFloat(userData.base_salary) || 0);
          // setLectures_count(parseFloat(userData.base_lectures_count) || 0);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        salary: Number(salary),
        lectures_count: Number(lectures_count),
        addition: Number(addition),
        deduction: Number(deduction),
        reason: reason || null,
        salary_month: date || new Date().toISOString().split("T")[0],
      };

      const url = editMode
        ? `https://api.clever-school.com/api/teachers/salary/${editingSalaryId}`
        : `https://api.clever-school.com/api/teachers/${id}/create-salary`;

      const method = editMode ? "put" : "post";

      await axios[method](url, payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      message.success(t(editMode ? "salaryUpdated" : "salaryCreated"));

      // Refresh data
      const refreshResponse = await axios.get(
        `https://api.clever-school.com/api/teachers/${id}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStudentData({
        ...studentData,
        salaries: refreshResponse.data.salaries || [],
      });

      // Reset form if not in edit mode
      if (!editMode) {
        setAddition(0);
        setDeduction(0);
        setReason("");
      } else {
        // Exit edit mode
        setEditMode(false);
        setEditingSalaryId(null);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error(error.response?.data?.message || t("errorOccurred"));
    }
  };

  const handleEdit = (salaryItem) => {
    setEditMode(true);
    setEditingSalaryId(salaryItem.id);
    setSalary(salaryItem.salary);
    setLectures_count(salaryItem.lectures_count);
    setAddition(salaryItem.addition);
    setDeduction(salaryItem.deduction);
    setReason(salaryItem.reason);
    setDate(salaryItem.date || new Date().toISOString().split("T")[0]);
  };

  const handleDeleteClick = (salaryId) => {
    setSalaryToDelete(salaryId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `https://api.clever-school.com/api/teachers/salary/${salaryToDelete}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("salaryDeleted"));

      // Refresh data
      const response = await axios.get(
        `https://api.clever-school.com/api/teachers/${id}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStudentData({
        ...studentData,
        salaries: response.data.salaries || [],
      });
    } catch (error) {
      console.error("Error deleting salary:", error);
      message.error(t("deleteError"));
    } finally {
      setOpenDeleteDialog(false);
      setSalaryToDelete(null);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingSalaryId(null);
    setSalary(parseFloat(studentData.base_salary) || 0);
    setLectures_count(parseFloat(studentData.base_lectures_count) || 0);
    setAddition(0);
    setDeduction(0);
    setReason("");
    setDate(new Date().toISOString().split("T")[0]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("notAvailable");
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language);
  };

  const salaryTotals = (studentData.salaries || []).reduce(
    (acc, item) => ({
      baseSalary: acc.baseSalary + parseFloat(item.salary || 0),
      lecturesCount: acc.lecturesCount + parseFloat(item.lectures_count || 0),
      additions:
        acc.additions +
        parseFloat(item.addition || 0) * parseFloat(item.lectures_count || 0),
      deductions: acc.deductions + parseFloat(item.deduction || 0),
      finalSalary: acc.finalSalary + parseFloat(item.final_salary || 0),
    }),
    {
      baseSalary: 0,
      lecturesCount: 0,
      additions: 0,
      deductions: 0,
      finalSalary: 0,
    }
  );

  const totalRowSx = {
    background: "#ffffff",
    "& td": {
      fontWeight: 700,
      borderTop: "3px solid #2196f3",
    },
  };

  return (
    <Box
      sx={{
        direction,
        textAlign,
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(2),
        },
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Teacher Details Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("TeacherInformation")}
          </Typography>

          <Grid container spacing={3}>
            {[
              { label: t("name"), value: studentData.name },
              { label: t("phoneNumber"), value: studentData.phone_number },
              {
                label: t("baseSalary"),
                value: parseFloat(studentData.base_salary).toLocaleString(
                  "en-US"
                ),
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="h6">
                      {item.label.includes("salary")
                        ? `${item.value}`
                        : item.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Salary History Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("salaryHistory")}
          </Typography>

          {studentData.salaries?.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("date")}</TableCell>
                    <TableCell>{t("baseSalary")}</TableCell>
                    <TableCell>{t("LecturesCount")}</TableCell>
                    <TableCell>{t("additions")}</TableCell>
                    <TableCell>{t("deductions")}</TableCell>
                    <TableCell>{t("finalSalary")}</TableCell>
                    <TableCell>{t("reason")}</TableCell>
                    <TableCell>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentData.salaries.map((salaryItem) => (
                    <TableRow key={salaryItem.id}>
                      <TableCell>
                        {formatDate(salaryItem.salary_month)}
                      </TableCell>
                      <TableCell>
                        {parseFloat(salaryItem.salary).toLocaleString("en-US")}
                      </TableCell>
                      <TableCell>{salaryItem.lectures_count}</TableCell>
                      <TableCell>
                        {(
                          parseFloat(salaryItem.addition) *
                          parseFloat(salaryItem.lectures_count)
                        ).toLocaleString("en-US")}
                      </TableCell>
                      <TableCell>
                        {parseFloat(salaryItem.deduction).toLocaleString(
                          "en-US"
                        )}
                      </TableCell>
                      <TableCell>
                        {parseFloat(salaryItem.final_salary).toLocaleString(
                          "en-US"
                        )}
                      </TableCell>
                      <TableCell>{salaryItem.reason || t("-")}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleEdit(salaryItem)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(salaryItem.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={totalRowSx}>
                    <TableCell>{t("total")}</TableCell>
                    <TableCell>
                      {salaryTotals.baseSalary.toLocaleString("en-US")}
                    </TableCell>
                    <TableCell>{salaryTotals.lecturesCount}</TableCell>
                    <TableCell>
                      {salaryTotals.additions.toLocaleString("en-US")}
                    </TableCell>
                    <TableCell>
                      {salaryTotals.deductions.toLocaleString("en-US")}
                    </TableCell>
                    <TableCell>
                      {salaryTotals.finalSalary.toLocaleString("en-US")}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" color="textSecondary">
              {t("noSalaryRecords")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Salary Adjustment Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 500,
              mb: 4,
              color: theme.palette.secondary.main,
            }}
          >
            {editMode ? t("editSalary") : t("SalaryAdjustment")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Salary")}
                type="number"
                value={salary}
                onChange={(e) => setSalary(parseFloat(e.target.value))}
                variant="outlined"
                helperText={t("baseSalaryHelper")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("LecturesCount")}
                type="number"
                value={lectures_count}
                onChange={(e) => setLectures_count(parseFloat(e.target.value))}
                variant="outlined"
                helperText={t("lecturesHelper")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("additions")}
                type="number"
                value={addition}
                onChange={(e) => setAddition(parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: (
                    <Add sx={{ mr: 1, color: theme.palette.success.main }} />
                  ),
                }}
                variant="outlined"
                helperText={t("additionsHelper")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("deductions")}
                type="number"
                value={deduction}
                onChange={(e) => setDeduction(parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: (
                    <Remove sx={{ mr: 1, color: theme.palette.error.main }} />
                  ),
                }}
                variant="outlined"
                helperText={t("deductionsHelper")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("date")}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarToday
                      sx={{ mr: 1, color: theme.palette.text.secondary }}
                    />
                  ),
                }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("reason")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Description
                      sx={{ mr: 1, color: theme.palette.text.secondary }}
                    />
                  ),
                }}
                helperText={t("reasonHelper")}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {editMode && (
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                {t("cancel")}
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {editMode ? t("update") : t("submit")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>{t("deleteSalaryConfirmation")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Salary;
